/**
 * Device-targeting API client — thin TanStack-Query-friendly wrappers around
 * the /api/v1/campaign-device-targeting endpoints defined by the API worker
 * (see apps/api/src/routes/campaign-device-targeting.ts).
 *
 * The underlying route currently models an allowlist only; the UI exposes a
 * mode toggle (allow/deny) that the form layer will translate into an
 * allowlist — "deny" = the complement of {mobile, desktop, tablet} minus the
 * selected devices. This keeps the server contract simple while letting
 * advertisers think in either direction.
 */

import { apiClient } from "@/api-client";

export const DEVICE_TYPES = ["mobile", "desktop", "tablet"] as const;
export type DeviceType = (typeof DEVICE_TYPES)[number];

export type TargetingMode = "allow" | "deny";

export interface DeviceTargeting {
	id: string;
	tenant_id: string;
	campaign_id: string;
	devices: DeviceType[];
	created_at: number;
	updated_at: number;
}

export interface DeviceTargetingResponse {
	targeting: DeviceTargeting;
}

export interface DeviceTargetingError {
	error: string;
}

// ---------------------------------------------------------------------------
// Mode translation helpers
//   allow mode: `selected` == allowed devices.
//   deny  mode: allowed devices = {mobile,desktop,tablet} \ selected.
// ---------------------------------------------------------------------------

export function selectedToAllowlist(
	selected: readonly DeviceType[],
	mode: TargetingMode,
): DeviceType[] {
	const set = new Set<DeviceType>(selected);
	if (mode === "allow") {
		return DEVICE_TYPES.filter((d) => set.has(d));
	}
	return DEVICE_TYPES.filter((d) => !set.has(d));
}

export function allowlistToSelected(
	allowlist: readonly DeviceType[],
	mode: TargetingMode,
): DeviceType[] {
	const set = new Set<DeviceType>(allowlist);
	if (mode === "allow") {
		return DEVICE_TYPES.filter((d) => set.has(d));
	}
	return DEVICE_TYPES.filter((d) => !set.has(d));
}

// ---------------------------------------------------------------------------
// Endpoint wrappers
// ---------------------------------------------------------------------------

const BASE = "/campaign-device-targeting";

export async function fetchDeviceTargeting(
	campaignId: string,
): Promise<DeviceTargeting | null> {
	try {
		const data = await apiClient.get<DeviceTargetingResponse>(
			`${BASE}/${encodeURIComponent(campaignId)}`,
		);
		return data.targeting;
	} catch (err) {
		// 404 = no rule configured (open-by-default). Surface as `null`.
		if (
			typeof err === "object" &&
			err !== null &&
			"status" in err &&
			(err as { status: number }).status === 404
		) {
			return null;
		}
		throw err;
	}
}

export async function upsertDeviceTargeting(input: {
	campaignId: string;
	devices: DeviceType[];
}): Promise<DeviceTargeting> {
	const data = await apiClient.post<DeviceTargetingResponse>(BASE, {
		campaignId: input.campaignId,
		devices: input.devices,
	});
	return data.targeting;
}

export async function deleteDeviceTargeting(
	campaignId: string,
): Promise<void> {
	await apiClient.delete(`${BASE}/${encodeURIComponent(campaignId)}`);
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const deviceTargetingKeys = {
	all: ["device-targeting"] as const,
	byCampaign: (campaignId: string) =>
		["device-targeting", "campaign", campaignId] as const,
};
